export const GAME_2048_BOARD_SIZE = 4;
export const GAME_2048_CELL_COUNT = GAME_2048_BOARD_SIZE * GAME_2048_BOARD_SIZE;
export const GAME_2048_MAX_TILE = 2 ** 17;
export const GAME_2048_TARGETS = [512, 1024, 2048, 4096, 8192, 16384, 32768, 65536, GAME_2048_MAX_TILE] as const;

export type Game2048Difficulty = 1 | 2 | 3 | 4;
export type Game2048Direction = 'up' | 'down' | 'left' | 'right';
export type Game2048Outcome = 'active' | 'lost' | 'topped';

export interface Game2048EngineState {
  board: number[];
  score: number;
  moves: number;
  rngState: number;
}

export interface Game2048MoveResult {
  state: Game2048EngineState;
  moved: boolean;
  motions: Game2048TileMotion[];
  spawnedTile: Game2048SpawnedTile | null;
}

export interface Game2048TileMotion {
  fromIndex: number;
  toIndex: number;
  value: number;
  merged: boolean;
}

export interface Game2048SpawnedTile {
  index: number;
  value: number;
}

const UINT32_RANGE = 0x100000000;
const ENGINE_STATE_KEYS = ['board', 'moves', 'rngState', 'score'];

export const get2048Target = (difficulty: number): number => {
  switch (difficulty) {
    case 1:
      return 512;
    case 2:
      return 1024;
    case 3:
      return 2048;
    case 4:
      return 4096;
    default:
      throw new Error(`Invalid 2048 difficulty: ${difficulty}`);
  }
};

const nextRandom = (state: number): [number, number] => {
  const nextState = (Math.imul(state, 1664525) + 1013904223) >>> 0;
  return [nextState / UINT32_RANGE, nextState];
};

const seedRandomState = (seed: number): number => {
  if (!Number.isSafeInteger(seed) || seed < 0) {
    throw new Error(`Invalid 2048 seed: ${seed}`);
  }

  const upper = Math.floor(seed / UINT32_RANGE) >>> 0;
  const lower = seed >>> 0;
  return (upper ^ lower ^ 0x9e3779b9) >>> 0;
};

const isTileValue = (value: number): boolean => {
  return value === 0 || (
    Number.isInteger(value) &&
    value >= 2 &&
    value <= GAME_2048_MAX_TILE &&
    (value & (value - 1)) === 0
  );
};

export const get2048Progress = (highestTile: number): {
  target: number | null;
  difficulty: Game2048Difficulty;
} => {
  if (!isTileValue(highestTile)) {
    throw new Error(`Invalid 2048 highest tile: ${highestTile}`);
  }
  const target = GAME_2048_TARGETS.find(value => value > highestTile) ?? null;
  const difficulty = highestTile < 512 ? 1 : highestTile < 1024 ? 2 : highestTile < 2048 ? 3 : 4;
  return { target, difficulty };
};

const isEngineStateRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

const hasExactEngineStateKeys = (state: Record<string, unknown>): boolean => {
  return Object.keys(state).sort().join(',') === ENGINE_STATE_KEYS.join(',');
};

const is2048Board = (value: unknown): value is number[] => {
  return Array.isArray(value) &&
    value.length === GAME_2048_CELL_COUNT &&
    value.every(tile => typeof tile === 'number' && isTileValue(tile));
};

const isNonNegativeSafeInteger = (value: unknown): value is number => {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
};

const isUint32 = (value: unknown): value is number => {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value < UINT32_RANGE;
};

export const validate2048State = (value: unknown): Game2048EngineState => {
  if (!isEngineStateRecord(value)) {
    throw new Error('Invalid saved 2048 state');
  }

  if (
    !hasExactEngineStateKeys(value) ||
    !is2048Board(value.board) ||
    !isNonNegativeSafeInteger(value.score) ||
    !isNonNegativeSafeInteger(value.moves) ||
    !isUint32(value.rngState)
  ) {
    throw new Error('Invalid saved 2048 state');
  }

  return {
    board: [...value.board],
    score: value.score,
    moves: value.moves,
    rngState: value.rngState
  };
};

export const parse2048State = (serialized: string): Game2048EngineState => {
  return validate2048State(JSON.parse(serialized));
};

interface SpawnResult {
  state: Game2048EngineState;
  spawnedTile: Game2048SpawnedTile;
}

const spawnTile = (state: Game2048EngineState): SpawnResult => {
  const emptyIndexes = state.board.reduce<number[]>((indexes, tile, index) => {
    if (tile === 0) {
      indexes.push(index);
    }
    return indexes;
  }, []);

  if (emptyIndexes.length === 0) {
    throw new Error('Cannot spawn a tile on a full 2048 board');
  }

  const [positionRandom, positionState] = nextRandom(state.rngState);
  const [valueRandom, valueState] = nextRandom(positionState);
  const position = emptyIndexes[Math.floor(positionRandom * emptyIndexes.length)];
  const board = [...state.board];
  board[position] = valueRandom < 0.9 ? 2 : 4;

  return {
    state: { ...state, board, rngState: valueState },
    spawnedTile: { index: position, value: board[position] }
  };
};

export const create2048State = (seed: number): Game2048EngineState => {
  const emptyState: Game2048EngineState = {
    board: Array<number>(GAME_2048_CELL_COUNT).fill(0),
    score: 0,
    moves: 0,
    rngState: seedRandomState(seed)
  };
  const firstSpawn = spawnTile(emptyState);
  return spawnTile(firstSpawn.state).state;
};

interface CollapsedLine {
  line: number[];
  score: number;
  motions: Game2048TileMotion[];
}

const collapseLine = (board: number[], indexes: number[]): CollapsedLine => {
  const tiles = indexes
    .map(index => ({ value: board[index], fromIndex: index }))
    .filter(tile => tile.value !== 0);
  const merged: number[] = [];
  const motions: Game2048TileMotion[] = [];
  let score = 0;

  for (let index = 0; index < tiles.length; index += 1) {
    const destination = indexes[merged.length];
    if (tiles[index].value === tiles[index + 1]?.value) {
      const value = tiles[index].value * 2;
      merged.push(value);
      score += value;
      motions.push(
        { fromIndex: tiles[index].fromIndex, toIndex: destination, value: tiles[index].value, merged: true },
        { fromIndex: tiles[index + 1].fromIndex, toIndex: destination, value: tiles[index + 1].value, merged: true }
      );
      index += 1;
    } else {
      merged.push(tiles[index].value);
      motions.push({
        fromIndex: tiles[index].fromIndex,
        toIndex: destination,
        value: tiles[index].value,
        merged: false
      });
    }
  }

  return {
    line: [...merged, ...Array<number>(GAME_2048_BOARD_SIZE - merged.length).fill(0)],
    score,
    motions
  };
};

const getLineIndexes = (lineIndex: number, direction: Game2048Direction): number[] => {
  const indexes = Array.from({ length: GAME_2048_BOARD_SIZE }, (_, offset) => {
    if (direction === 'left' || direction === 'right') {
      return lineIndex * GAME_2048_BOARD_SIZE + offset;
    }
    return offset * GAME_2048_BOARD_SIZE + lineIndex;
  });

  if (direction === 'right' || direction === 'down') {
    indexes.reverse();
  }
  return indexes;
};

const writeLine = (
  board: number[],
  line: number[],
  indexes: number[]
): void => {
  indexes.forEach((boardIndex, offset) => {
    board[boardIndex] = line[offset];
  });
};

export const move2048 = (
  state: Game2048EngineState,
  direction: Game2048Direction
): Game2048MoveResult => {
  validate2048State(state);
  assert2048Active(state);
  const board = [...state.board];
  const motions: Game2048TileMotion[] = [];
  let scoreIncrease = 0;

  for (let lineIndex = 0; lineIndex < GAME_2048_BOARD_SIZE; lineIndex += 1) {
    const indexes = getLineIndexes(lineIndex, direction);
    const collapsed = collapseLine(state.board, indexes);
    writeLine(board, collapsed.line, indexes);
    scoreIncrease += collapsed.score;
    motions.push(...collapsed.motions);
  }

  const moved = board.some((tile, index) => tile !== state.board[index]);
  if (!moved) {
    return { state, moved: false, motions: [], spawnedTile: null };
  }

  const nextState = validate2048State({
    board,
    score: state.score + scoreIncrease,
    moves: state.moves + 1,
    rngState: state.rngState
  });
  const feedback = motions.filter(motion => motion.fromIndex !== motion.toIndex || motion.merged);
  if (get2048Outcome(board) === 'topped') {
    return { state: nextState, moved: true, motions: feedback, spawnedTile: null };
  }
  const spawn = spawnTile(nextState);
  return {
    state: spawn.state,
    moved: true,
    motions: feedback,
    spawnedTile: spawn.spawnedTile
  };
};

export const double2048Tiles = (state: Game2048EngineState): Game2048MoveResult => {
  validate2048State(state);
  assert2048Active(state);
  const board = state.board.map(value => value * 2);
  const motions = state.board.flatMap((value, index) => value === 0 ? [] : [{
    fromIndex: index,
    toIndex: index,
    value,
    merged: true
  }]);
  if (motions.length === 0) {
    return { state, moved: false, motions: [], spawnedTile: null };
  }
  return {
    state: validate2048State({
      ...state,
      board,
      score: state.score + board.reduce((sum, value) => sum + value, 0),
      moves: state.moves + 1
    }),
    moved: true,
    motions,
    spawnedTile: null
  };
};

export const get2048LargestTile = (board: number[]): number => {
  return Math.max(...board);
};

export const hasReached2048Target = (board: number[], difficulty: number): boolean => {
  return get2048LargestTile(board) >= get2048Target(difficulty);
};

export const is2048GameOver = (board: number[]): boolean => {
  if (board.some(tile => tile === 0)) {
    return false;
  }

  for (let row = 0; row < GAME_2048_BOARD_SIZE; row += 1) {
    for (let column = 0; column < GAME_2048_BOARD_SIZE; column += 1) {
      const index = row * GAME_2048_BOARD_SIZE + column;
      if (
        (column + 1 < GAME_2048_BOARD_SIZE && board[index] === board[index + 1]) ||
        (row + 1 < GAME_2048_BOARD_SIZE && board[index] === board[index + GAME_2048_BOARD_SIZE])
      ) {
        return false;
      }
    }
  }

  return true;
};

export const get2048Outcome = (board: number[]): Game2048Outcome => {
  if (get2048LargestTile(board) === GAME_2048_MAX_TILE) {
    return 'topped';
  }
  return is2048GameOver(board) ? 'lost' : 'active';
};

const assert2048Active = (state: Game2048EngineState): void => {
  if (get2048Outcome(state.board) !== 'active') {
    throw new Error('Cannot act on a finished 2048 run');
  }
};
